import { AuthenticationGuard } from '@trimble/trimble-id-solidjs';
import { JSX } from 'solid-js';
import {
  ApiServiceProvider,
  ApiServiceProviderProps,
} from './ApiServiceProvider';
import { AppContextProvider } from './AppContextProvider';
import { DataContextProvider } from './data-state/DataContextProvider';
import { CapabilityRegistryProvider } from './CapabilityRegistryProvider';
import { FeatureExtractionContextProvider } from './feature-extraction/FeatureExtractionContextProvider';
import { FxWorkspaceRepositoryProvider } from './fx-workspace/FxWorkspaceRepositoryProvider';
import { ThemeContextProvider } from './ThemeContextProvider';
import { ProjectSettingsProvider } from './ProjectSettingsProvider';
import { LocaleProvider } from './LocaleProvider';
import { TranslationProviderWithUserLanguage } from './TranslationProviderWithUserLanguage';
import { ConnectProjectProvider } from '@trimble/connect-project-providers';
import { MapTileConfigProvider } from './MapTileConfigProvider';
import { type ConfigFeatureFlagValues } from '@trimble/feature-flags';
import { FeatureFlagOverridesProvider } from './FeatureFlagOverridesProvider';

export interface ProtectedLayoutProviderProps extends ApiServiceProviderProps {
  envName: string;
  deployedTag?: string;
  connectUrl: string;
  mapTileServiceBaseUrl: string;
  mapTileLayerQueryParams: string;
  mapTileAuthToken: string;
  fxWorkspaceDomain?: string;
  fxMapsServiceBaseUrl?: string;
  fxFeaturesServiceBaseUrl?: string;
  fxTilesServiceBaseUrl?: string;
  featureFlags?: ConfigFeatureFlagValues;
}

export function ProtectedLayoutProvider(
  props: Readonly<ProtectedLayoutProviderProps>
): JSX.Element {
  return (
    <AuthenticationGuard>
      <ApiServiceProvider
        alignmentsDomain={props.alignmentsDomain}
        mobileMappingDomain={props.mobileMappingDomain}
        seuratDomain={props.seuratDomain}
        imagingDomain={props.imagingDomain}
        tcTcpsDomain={props.tcTcpsDomain}
        tcUserDomain={props.tcUserDomain}
        ecomDomain={props.ecomDomain}
        coordinatesDomain={props.coordinatesDomain}
        splatsDomain={props.splatsDomain}
        clientName={props.clientName}
        clientVersion={props.clientVersion}
        isProductionEnvironment={props.isProductionEnvironment}
      >
        <ConnectProjectProvider connectUrl={props.connectUrl}>
          <MapTileConfigProvider
            mapTileServiceBaseUrl={props.mapTileServiceBaseUrl}
            mapTileLayerQueryParams={props.mapTileLayerQueryParams}
            mapTileAuthToken={props.mapTileAuthToken}
          >
            <LocaleProvider>
              <TranslationProviderWithUserLanguage>
                <ThemeContextProvider>
                  <FeatureFlagOverridesProvider defaults={props.featureFlags}>
                    <AppContextProvider
                      envName={props.envName}
                      deployedTag={props.deployedTag}
                    >
                      <ProjectSettingsProvider>
                        <DataContextProvider>
                          <CapabilityRegistryProvider>
                            <FeatureExtractionContextProvider
                              fxMapsServiceBaseUrl={props.fxMapsServiceBaseUrl}
                              fxFeaturesServiceBaseUrl={
                                props.fxFeaturesServiceBaseUrl
                              }
                              fxTilesServiceBaseUrl={
                                props.fxTilesServiceBaseUrl
                              }
                            >
                              <FxWorkspaceRepositoryProvider
                                fxWorkspaceDomain={props.fxWorkspaceDomain}
                                clientName={props.clientName}
                                clientVersion={props.clientVersion}
                              >
                                {props.children}
                              </FxWorkspaceRepositoryProvider>
                            </FeatureExtractionContextProvider>
                          </CapabilityRegistryProvider>
                        </DataContextProvider>
                      </ProjectSettingsProvider>
                    </AppContextProvider>
                  </FeatureFlagOverridesProvider>
                </ThemeContextProvider>
              </TranslationProviderWithUserLanguage>
            </LocaleProvider>
          </MapTileConfigProvider>
        </ConnectProjectProvider>
      </ApiServiceProvider>
    </AuthenticationGuard>
  );
}
